import React, {useState} from 'react';
import RewardSetup from '@/containers/Rewards/RewardSetup/RewardSetup';
import RewardDisplay from '@/containers/Rewards/RewardDisplay/RewardDisplay';
import RewardAttributes from '@/containers/Rewards/RewardAttributes/RewardAttributes';
import RewardReview from '@/containers/Rewards/RewardReview/RewardReview';
import {RewardFlowWrapper} from '@/containers/Rewards/common/components';
import {useRewardsCreate} from '@/containers/Rewards/common/hooks';
import {SECTION_TITLES} from '@/containers/Rewards/RewardReview/components/common/ReviewConstants';
import {DISABLE_PUBLISH_REWARD_STATUS} from '@/utils/constants/rewardsConstants';

const {CREATE_REWARDS} = SECTION_TITLES.DISPLAY;

/**
 * CreateReward component - Create flow only
 * Handles new reward creation with full step navigation
 */
const CreateReward = () => {
  const [isCancel, setIsCancel] = useState(false);
  const [isNameValid, setIsNameValid] = useState(true);

  const {
    formData,
    currentStep,
    setStep,
    handleNext,
    handleDraft,
    handleCancel,
    hasFormErrors,
    disableDraft,
    isLoading
  } = useRewardsCreate();

  const handleCancelClick = () => {
    setIsCancel(true);
  };

  const handleCancelConfirm = () => {
    handleCancel();
    setIsCancel(false);
  };

  const handleCancelReject = () => {
    setIsCancel(false);
  };

  const handleNextClick = async () => {
    const result = await handleNext();
    if (result?.success === false && result?.isDuplicateName) {
      setIsNameValid(false);
    }
  };

  // Single centralised save-draft handler (footer + child components)
  const saveDraft = async ({redirectOnSuccess = true} = {}) => {
    const result = await handleDraft({redirectOnSuccess});
    if (result?.success === false && result?.isDuplicateName) {
      setIsNameValid(false);
    }
  };

  // Render step content based on current step
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <RewardSetup onSaveDraft={saveDraft} mode='create' />;
      case 2:
        return <RewardDisplay />;
      case 3:
        return <RewardAttributes />;
      case 4:
        return <RewardReview mode='create' />;
      default:
        return <RewardSetup onSaveDraft={saveDraft} mode='create' />;
    }
  };

  // Get step titles for progress indicator
  const stepTitles = [
    {title: 'Setup'},
    {title: 'Display'},
    {title: 'Attributes'},
    {title: 'Review'}
  ];

  const isReviewStep = currentStep === 4;
  const isPublishDisabled =
    isReviewStep &&
    !!formData?.status &&
    DISABLE_PUBLISH_REWARD_STATUS.includes(formData.status);
  const footerFormError = isReviewStep
    ? hasFormErrors() || isPublishDisabled
    : hasFormErrors();

  return (
    <RewardFlowWrapper
      mode='create'
      title={CREATE_REWARDS}
      breadcrumbLabel={CREATE_REWARDS}
      showProgressIndicator={true}
      showFooter={true}
      footerProps={{
        disableDraft
      }}
      onCancel={handleCancelClick}
      onNext={handleNextClick}
      onDraft={saveDraft}
      isLoading={isLoading}
      formErrors={footerFormError}
      currentStep={currentStep}
      stepTitles={stepTitles}
      onStepClick={(step) => setStep(step)}
      isEditDisabled={false}
      showDownload={false}
      isNameValid={isNameValid}
      onNameValidChange={setIsNameValid}
      isCancel={isCancel}
      onCancelConfirm={handleCancelConfirm}
      onCancelReject={handleCancelReject}>
      {renderStepContent()}
    </RewardFlowWrapper>
  );
};

export default CreateReward;
